export interface Selection {
  source: string;
  label: string;
  value: string;
  tag?: string;
  bad?: boolean;
  clientX: number;
  clientY: number;
}

export interface Verdict {
  kind: 'mismatch' | 'match' | 'info';
  text: string;
  detail?: string;
  finding?: string;
}

/** Parses MM.DD.YYYY into a sortable number, or null. */
function parseDate(value: string): number | null {
  const m = value.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (!m) return null;
  return Number(m[3]) * 10000 + Number(m[1]) * 100 + Number(m[2]);
}

export class InspectController {
  constructor(private readonly today: string) {}

  reset(): void {
    // No cross-encounter state to clear anymore; kept for call-site compatibility.
  }

  /** Every click is self-contained: show what the field says, right where it lives. */
  select(sel: Selection): Verdict {
    if (sel.tag === 'expiry') {
      const date = parseDate(sel.value);
      const today = parseDate(this.today);
      if (date !== null && today !== null && date < today) {
        return {
          kind: 'mismatch',
          text: 'EXPIRED',
          detail: `${sel.source} · ${sel.label}: ${sel.value}, before today (${this.today})`,
          finding: `${sel.source}: ${sel.label} ${sel.value}, EXPIRED`,
        };
      }
      return {
        kind: 'match',
        text: `${sel.label}: ${sel.value}`,
        detail: `${sel.source} · valid as of today (${this.today})`,
      };
    }

    if (sel.tag === 'seal') {
      if (sel.value !== 'authentic') {
        return {
          kind: 'mismatch',
          text: 'SEAL IS FORGED, NO REVENUE SHARE ON FILE',
          detail: `${sel.source}: forged seal, this studio is not paying us our cut`,
          finding: `${sel.source}: forged revenue-share seal (skipping our cut)`,
        };
      }
      return { kind: 'match', text: 'Seal is genuine. This studio pays us our cut.', detail: sel.source };
    }

    if (sel.tag === 'face') {
      return { kind: 'info', text: sel.label, detail: sel.source };
    }

    // Everyday field: show exactly what it says. Ground-truth `bad` fields get flagged.
    if (sel.bad) {
      return {
        kind: 'mismatch',
        text: `${sel.label}: ${sel.value}`,
        detail: `${sel.source} · flagged, doesn't add up`,
        finding: `${sel.source}: ${sel.label} ${sel.value}`,
      };
    }
    return { kind: 'info', text: `${sel.label}: ${sel.value}`, detail: sel.source };
  }
}
